import React, { useEffect, useMemo, useState } from "react";
import { Alert, Checkbox, Form, Input, Modal, Table } from "antd";
import { useTranslation } from "react-i18next";
import { assignLocationsSequentially, parseRackLayout } from "functions/pickRoute";

/**
 * "Vakken doornummeren": give the vakken codes in route order in one go - 1A, 1B ... 1H, 2A -
 * instead of one by one. The number of vakken per stelling can differ per stelling ("8, 8, 6"):
 * the warehouse has basket racks of 8 and of 6. Shows what it is about to do before it does it.
 */
const NumberingModal = ({ open, entries, onCancel, onConfirm, busy }) => {
  const { t } = useTranslation();
  const [startCode, setStartCode] = useState("1A");
  const [layoutText, setLayoutText] = useState("8");
  const [onlyEmpty, setOnlyEmpty] = useState(true);

  useEffect(() => {
    if (open) setOnlyEmpty(true);
  }, [open]);

  const layout = useMemo(() => parseRackLayout(layoutText), [layoutText]);
  const plan = useMemo(
    () =>
      open && layout.ok
        ? assignLocationsSequentially(entries, { startCode, rackLayout: layout.layout, onlyEmpty })
        : { ok: true, changes: [] },
    [open, entries, startCode, layout, onlyEmpty],
  );
  const labelOf = useMemo(() => new Map((entries || []).map((entry) => [entry.id, entry.label || "-"])), [entries]);

  return (
    <Modal
      open={open}
      title={t("pick_route.numbering_title")}
      okText={t("pick_route.numbering_apply", { count: plan.changes.length })}
      cancelText={t("cancel")}
      okButtonProps={{ disabled: !layout.ok || !plan.ok || plan.changes.length === 0, loading: busy }}
      onOk={() => onConfirm(plan.changes)}
      onCancel={onCancel}
      width={560}
    >
      <p style={{ color: "#6b6b6b" }}>{t("pick_route.numbering_help")}</p>
      <Form layout="inline" className="mb-3">
        <Form.Item label={t("pick_route.numbering_start")}>
          <Input value={startCode} onChange={(event) => setStartCode(event.target.value)} style={{ width: 90, fontFamily: "monospace" }} />
        </Form.Item>
        <Form.Item label={t("pick_route.numbering_letters")}>
          <Input value={layoutText} onChange={(event) => setLayoutText(event.target.value)} style={{ width: 160 }} status={layout.ok ? "" : "error"} />
        </Form.Item>
      </Form>
      <p style={{ color: "#6b6b6b", marginTop: -8 }}>{t("pick_route.numbering_letters_help")}</p>
      {!layout.ok && <Alert type="error" showIcon className="mb-3" message={t("pick_route.numbering_letters_invalid")} />}
      <Checkbox checked={onlyEmpty} onChange={(event) => setOnlyEmpty(event.target.checked)} className="mb-3">
        {t("pick_route.numbering_only_empty")}
      </Checkbox>
      {!onlyEmpty && <Alert type="warning" showIcon className="mb-3" message={t("pick_route.numbering_all_warning")} />}
      {!plan.ok && <Alert type="error" showIcon className="mb-3" message={t("pick_route.location_invalid")} />}
      {plan.unassigned > 0 && (
        <Alert type="warning" showIcon className="mb-3" message={t("pick_route.numbering_unassigned", { count: plan.unassigned })} />
      )}
      <Table
        size="small"
        rowKey="entryId"
        pagination={{ pageSize: 8, hideOnSinglePage: true }}
        dataSource={plan.changes}
        columns={[
          { title: t("pick_route.location"), dataIndex: "location", width: 90, render: (code) => <strong style={{ fontFamily: "monospace" }}>{code}</strong> },
          { title: t("pick_route.sign_text"), dataIndex: "entryId", render: (id) => labelOf.get(id) },
        ]}
      />
    </Modal>
  );
};

export default NumberingModal;
